import React from 'react';

import styles from './DialogDevicesPatient.module.css';
import { ImCross } from 'react-icons/im';

import { dateFormat } from '../../../services/parseDates';
import { getFullName } from '../../../services/getPatientAtribute';
import { getUser } from '../../../services/Auth';
import { getEnvironmentApiUrl } from '../../../services/General';

import { useEffect, useState } from 'react';
import useHttp from '../../../hooks/useHttp';
import { useToast } from '../../../hooks/useToast';

import Table from '../../UI/Table/Table';
import ErrorSpinnerWrapper from '../../UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';
import DialogDeviceSelect from '../DialogDeviceSelect/DialogDeviceSelect';
import Modal from '../../UI/Modal/Modal';

import { useTranslation } from 'react-i18next';

import { DeviceLoanPatient } from '../../../interfaces/Device';
import { Column } from '../../../interfaces/Columns';

interface DeleteDeviceBody {
    data: {
        return_date: string,
        return_user_id: number,
        deleted: true
    },
    filter: {
        id: number | undefined,
    }
}

const DialogDevicesPatient: React.FC<{
    disabledLoanDevices: boolean
    patient_id: number | undefined
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ disabledLoanDevices, patient_id, setShowModal }) => {

    const { t } = useTranslation();

    const toast = useToast();

    const [showSelectDeviceDialog, setShowSelectDeviceDialog] = useState(false);

    const COLUMNS_DEVICES: Column<DeviceLoanPatient>[] = [
        {
            id: 0,
            name: () => t('device'),
            visible: true,
            sortable: false,
            cell: (device) => device.device?.device_type?.description ?? '',
        },
        {
            id: 1,
            name: () => t('manufacturer'),
            visible: true,
            sortable: false,
            cell: (device) => device.device?.model?.manufacturer?.name ?? '',
        },
        {
            id: 2,
            name: () => t('model'),
            visible: true,
            sortable: false,
            cell: (device) => device.device?.model?.name ?? '',
        },
        {
            id: 3,
            name: () => t('loan_date'),
            visible: true,
            sortable: false,
            cell: (device) => device && dateFormat(device.loan_date),
        },
        {
            id: 4,
            name: () => t('pick_up_date'),
            visible: true,
            sortable: false,
            cell: (device) => device && dateFormat(device.return_date),
        },
        {
            id: 5,
            name: () => t('loan_professional'),
            visible: true,
            sortable: false,
            cell: (device) => getFullName(device.loan_user),
        },
        {
            id: 6,
            name: () => t('pick_up_professional'),
            visible: true,
            sortable: false,
            cell: (device) => getFullName(device.return_user),
        },
        {
            id: 7,
            name: () => '',
            visible: true,
            sortable: false,
            element: (device) => device &&
                <button
                    onClick={() => withdrawDevice(device)}
                    className={` ${styles.withdrawButton} button`}
                    disabled={device.return_date !== null || device.return_user !== null}
                >
                    <ImCross /> {t('withdraw')}
                </button>,
        }
    ];

    const [deleteBodies, setDeletedBodies] = useState<DeleteDeviceBody[]>([]);

    const { isLoading, error, sendRequest } = useHttp<DeviceLoanPatient[]>();
    const { sendRequest: withdrawDeviceRequest } = useHttp();

    const [devicesLoan, setDevicesLoan] = useState<DeviceLoanPatient[]>([]);

    const withdrawDevice = (deviceLoan: DeviceLoanPatient) => {
        const dateReturn = (new Date()).toISOString();
        setDeletedBodies(oldDeletedBodies => [...oldDeletedBodies, {
            data: {
                return_date: dateReturn,
                return_user_id: getUser().id,
                deleted: true
            },
            filter: {
                id: deviceLoan.id,
            },
        }]);

        setDevicesLoan(oldDevicesLoan => {
            const newDevices = [...oldDevicesLoan];
            return newDevices.map(oldDevice => {
                if (oldDevice.id === deviceLoan.id) {
                    const newDevice = { ...oldDevice };
                    return {
                        ...newDevice,
                        return_user: getUser(),
                        return_date: dateReturn
                    };
                }
                else return oldDevice;
            });
        });
    };

    const handleClickSave = () => {
        deleteBodies.forEach(deletedBody => {
            withdrawDeviceRequest({
                url: getEnvironmentApiUrl() + 'device_loan',
                method: 'PUT',
                body: deletedBody,
            }, () => {
                const device = devicesLoan.find(deviceLoan => deviceLoan.id === deletedBody.filter.id)?.device;
                toast.success(<p>{t('device')} <strong>{device?.device_type?.name} {device?.model?.manufacturer?.name} {device?.model?.name}</strong> {t('withdrawn_correctly')}</p>);
            }, () => {
                const device = devicesLoan.find(deviceLoan => deviceLoan.id === deletedBody.filter.id)?.device;
                toast.error(`${t('error_deleting')}: ${device?.device_type?.name} ${device?.model?.manufacturer?.name}  ${device?.model?.name}`);
            });
        });
        setShowModal(false);
    };

    const sortDevices = (a: DeviceLoanPatient, b: DeviceLoanPatient) => {
        if (a.return_date === b.return_date) return 0;
        if (a.return_date === null) return -1;
        if (b.return_date === null) return 1;
        else if (a.return_date && b.return_date) return b.return_date.localeCompare(a.return_date);
        else return 0;
    };

    useEffect(() => {
        sendRequest({
            url: getEnvironmentApiUrl() + 'device_loan/all_with_devices',
            method: 'POST',
            body: { params: {}, filter: { patient_id } },
        }, (data) => setDevicesLoan(data.sort(sortDevices)));
    }, [patient_id, sendRequest]);

    const getAllLoanDevices = () => {
        sendRequest({
            url: getEnvironmentApiUrl() + 'device_loan/all_with_devices',
            method: 'POST',
            body: { params: {}, filter: { patient_id } },
        }, (data) => setDevicesLoan(data.sort(sortDevices)));
    };

    return (
        <div className='dialog'>
            <div className={styles.devicesTitleContainer}>
                <h1 className='dialogTitle'>{t('devices')}</h1>
                <button
                    onClick={() => setShowSelectDeviceDialog(true)}
                    className={`button ${styles.lendButton}`}
                    disabled={disabledLoanDevices}
                >
                    {t('assign_device')}
                </button>
            </div>
            <div className={styles.tableContainer}>
                <ErrorSpinnerWrapper
                    loading={isLoading}
                    error={error}
                >
                    <Table
                        rows={devicesLoan}
                        columns={COLUMNS_DEVICES}
                        nohoverRow
                        preventSort
                    />
                </ErrorSpinnerWrapper>
            </div>
            <div className={styles.buttonsContainer}>
                <button onClick={() => setShowModal(false)} className={`button ${styles.cancel}`}>{t('cancel')}</button>
                <button onClick={handleClickSave} className={`button ${styles.confirm}`}>{t('save')}</button>
            </div>
            <Modal
                show={showSelectDeviceDialog}
                setShowModal={setShowSelectDeviceDialog}
                secondModal
            >
                <DialogDeviceSelect
                    showModal={setShowSelectDeviceDialog}
                    patientID={patient_id}
                    getAllLoanDevices={getAllLoanDevices}
                />
            </Modal>
        </div>
    );
};

export default DialogDevicesPatient;